import React, { useState } from "react";
import { Link, useNavigate, useOutletContext } from "react-router-dom";
import { Pencil, Loader2, AlertCircle, Plus, Trash2, Repeat, Star, Search } from "lucide-react";
import { Card, CardContent } from "@pgs-core/ui/components/card";
import { Badge } from "@pgs-core/ui/components/badge";
import { Button } from "@pgs-core/ui/components/button";
import { Input } from "@pgs-core/ui/components/input";
import { Label } from "@pgs-core/ui/components/label";
import { Alert, AlertTitle, AlertDescription } from "@pgs-core/ui/components/alert";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from "@pgs-core/ui/components/dialog";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from "@pgs-core/ui/components/alert-dialog";
import { PaginationControls } from "@pgs-core/ui/components/pagination-controls";
import { useApiQuery } from "@pgs-core/portal-lib/hooks";
import { useListControls } from "@pgs-core/portal-lib/list-controls";
import { PageHeader } from "@pgs-core/ui/components/page-header";
import {
  createEvent,
  deleteEvent,
  describeRecurrence,
  fetchEventsPage,
  parseEventRecurrence,
  type EventItem,
} from "../lib/events";
import type { ClientOutletContext } from "../lib/outlet-context";

function formatEventDate(event: EventItem): string {
  const date = new Date(`${event.date}T00:00:00`);
  if (Number.isNaN(date.getTime())) return event.date;
  const text = date.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short", year: "numeric" });
  return event.end_date && event.end_date !== event.date ? `${text} → ${event.end_date}` : text;
}

function formatEventTime(event: EventItem): string {
  if (event.all_day) return "All day";
  if (event.start_time && event.end_time) return `${event.start_time}–${event.end_time}`;
  if (event.start_time) return `starts ${event.start_time}`;
  if (event.end_time) return `until ${event.end_time}`;
  return "—";
}

export function EventsPage() {
  const { api } = useOutletContext<ClientOutletContext>();
  const navigate = useNavigate();
  const controls = useListControls();
  const query = useApiQuery(
    () => (api ? fetchEventsPage(api, controls.queryString) : null),
    [api, controls.queryString]
  );
  const events = query.data?.events ?? null;
  const total = query.data?.total ?? 0;
  const [error, setError] = useState("");

  const [createOpen, setCreateOpen] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newDate, setNewDate] = useState("");
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");

  const [deleteTarget, setDeleteTarget] = useState<EventItem | null>(null);
  const [deleting, setDeleting] = useState(false);

  const openCreate = () => {
    setNewTitle("");
    setNewDate("");
    setCreateError("");
    setCreateOpen(true);
  };

  const create = async () => {
    if (!api) return;
    if (!newTitle.trim() || !newDate) {
      setCreateError("Enter a title and a date.");
      return;
    }
    setCreating(true);
    setCreateError("");
    try {
      const result = await createEvent(api, { title: newTitle.trim(), date: newDate });
      setCreateOpen(false);
      navigate(`/events/${result.id}`);
    } catch (err) {
      setCreateError((err as Error).message);
    } finally {
      setCreating(false);
    }
  };

  const remove = async () => {
    if (!api || !deleteTarget) return;
    setDeleting(true);
    setError("");
    try {
      await deleteEvent(api, deleteTarget.id);
      query.reload();
      setDeleteTarget(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Events"
        description="One-off and repeating events. Published events appear on your site straight away — no publish step needed."
        actions={
          <Button onClick={openCreate} size="sm">
            <Plus className="h-4 w-4" />
            New event
          </Button>
        }
      />

      {(error || query.error) && (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Something went wrong</AlertTitle>
          <AlertDescription>{error || query.error}</AlertDescription>
        </Alert>
      )}

      <div className="relative mb-4 max-w-sm">
        <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={controls.search}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => controls.setSearch(e.target.value)}
          placeholder="Search events…"
          className="pl-8"
          aria-label="Search events"
        />
      </div>

      {!events && !query.error && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      )}

      {events && (
        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs text-muted-foreground">
                  <th className="px-4 py-3 font-medium">Event</th>
                  <th className="px-4 py-3 font-medium">Date</th>
                  <th className="px-4 py-3 font-medium">Time</th>
                  <th className="px-4 py-3 font-medium">Repeats</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {events.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">
                      {controls.q
                        ? "No events match your search."
                        : "No events yet — add your first event to get started."}
                    </td>
                  </tr>
                )}
                {events.map((event) => {
                  const recurrence = describeRecurrence(parseEventRecurrence(event.recurrence));
                  return (
                    <tr key={event.id} className="border-b last:border-0 hover:bg-muted/50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5 font-medium">
                          {event.title}
                          {event.featured === 1 && <Star className="h-3.5 w-3.5 text-warning" aria-label="Featured" />}
                        </div>
                        {event.location && <div className="text-xs text-muted-foreground">{event.location}</div>}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{formatEventDate(event)}</td>
                      <td className="px-4 py-3 text-muted-foreground">{formatEventTime(event)}</td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {recurrence ? (
                          <span className="flex items-center gap-1.5">
                            <Repeat className="h-3.5 w-3.5" />
                            {recurrence}
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant={event.published ? "success" : "secondary"}>
                          {event.published ? "Live" : "Draft"}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <Button asChild variant="outline" size="sm">
                            <Link to={`/events/${event.id}`}>
                              <Pencil className="h-3.5 w-3.5" />
                              Edit
                            </Link>
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-muted-foreground hover:text-destructive"
                            title="Delete event"
                            onClick={() => setDeleteTarget(event)}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <PaginationControls
              page={controls.page}
              total={total}
              perPage={controls.perPage}
              onPageChange={controls.setPage}
              onPerPageChange={controls.setPerPage}
              itemsLabel="events"
              className="border-t px-4 py-3"
            />
          </CardContent>
        </Card>
      )}

      {/* Create event */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New event</DialogTitle>
            <DialogDescription>
              New events start as a draft — add the details, then set it live.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="new-event-title">Title</Label>
              <Input
                id="new-event-title"
                value={newTitle}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewTitle(e.target.value)}
                placeholder="e.g. Quiz night"
                autoFocus
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new-event-date">Date</Label>
              <Input
                id="new-event-date"
                type="date"
                value={newDate}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewDate(e.target.value)}
              />
            </div>
            {createError && <p className="text-sm text-destructive">{createError}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} disabled={creating}>
              Cancel
            </Button>
            <Button onClick={() => { void create(); }} disabled={creating || !newTitle.trim() || !newDate}>
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              Create event
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open: boolean) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{deleteTarget?.title}"?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the event from your portal and your live site straight away. This can't
              be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e: React.MouseEvent) => { e.preventDefault(); void remove(); }}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              Delete event
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
